/**
 * 모듈 통신 점검 — CCTV 마다 루트(127)·BLE(128)·통로등(40~)이 기간 동안 얼마나 응답했는지.
 * 조명서버가 5분마다 모듈별로 최대 3번 ping 해 현장 Prometheus 에 남긴 기록을 읽는다(장비를 직접
 * 두드리지 않는다). 한 번도 응답 안 했으면 무응답, 응답했다 끊겼다 하면 불안정과 끊김 주기를 보인다.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

import {
  QaModuleReport,
  QaModuleVerdict,
  QaModuleWindow,
  SiteParkingQaService,
} from '../../services/SiteParkingQaService';
import {
  MODULE_VERDICT_COLOR,
  MODULE_VERDICT_LABEL,
  MODULE_VERDICT_ORDER,
  SCAN_STALE_SECONDS,
  describeModule,
  filterModuleCctvs,
  moduleLabel,
  moduleLevels,
} from './qaInspect';

interface Props {
  projectId: number;
  ip: string;
  /** CCTV 를 누르면 상세(연결 주차면·카메라 판정)를 연다. */
  onOpen: (name: string) => void;
  /** 점검 결과가 바뀔 때마다 — 맵이 문제 있는 것만 칠하는 데 쓴다. */
  onResult?: (result: QaModuleReport | null) => void;
}

const PERIODS: { value: QaModuleWindow; label: string }[] = [
  { value: '1h', label: '1시간' },
  { value: '24h', label: '24시간' },
  { value: '7d', label: '7일' },
];

const TEXT_COLOR = { error: 'error.main', warning: 'warning.main', success: 'success.main', default: 'text.secondary' };
const textColor = (v: QaModuleVerdict) => TEXT_COLOR[MODULE_VERDICT_COLOR[v]];

const QaModulePanel: React.FC<Props> = ({ projectId, ip, onOpen, onResult }) => {
  const [period, setPeriod] = useState<QaModuleWindow>('24h');
  const [report, setReport] = useState<QaModuleReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problemsOnly, setProblemsOnly] = useState(true);
  const [level, setLevel] = useState('');

  const check = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setReport(await SiteParkingQaService.modules(projectId, ip, period));
    } catch (e) {
      setError(e instanceof Error ? e.message : '모듈 통신 점검 실패');
    } finally {
      setBusy(false);
    }
  }, [projectId, ip, period]);

  useEffect(() => {
    check();
  }, [check]);
  useEffect(() => {
    onResult?.(report);
  }, [report, onResult]);

  const levels = useMemo(() => moduleLevels(report?.cctvs ?? []), [report]);
  const shown = useMemo(
    () => filterModuleCctvs(report?.cctvs ?? [], problemsOnly, level),
    [report, problemsOnly, level],
  );
  const scanAge = report && report.scanAt ? report.now - report.scanAt : 0;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }} data-testid="qa-modules">
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mr: 'auto' }}>
          CCTV 모듈 통신 점검
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
          onClick={check}
          disabled={busy}
          sx={{ minHeight: 44 }}
        >
          {busy ? '읽는 중…' : '다시 읽기'}
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" component="p">
        조명서버가 5분마다 CCTV 의 루트(127)·BLE(128)·통로등(40~)에 묻고 남긴 기록입니다. 기간 내내 한 번도
        응답 안 했으면 무응답, 응답률이 95% 미만이면 불안정입니다. 5분 사이의 짧은 끊김은 잡히지 않습니다. 통로등은
        사람이 지나가지 않으면 한동안 응답하지 않을 수 있어, 최근 3시간 내내 응답이 없을 때만 무응답으로 봅니다.
      </Typography>

      <Box
        sx={{
          mt: 1.5,
          display: 'grid',
          gap: 1,
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'auto auto 160px' },
          alignItems: 'center',
          justifyContent: { sm: 'start' },
        }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={period}
          onChange={(_, v: QaModuleWindow | null) => v && setPeriod(v)}
          aria-label="점검 기간"
          sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, '& .MuiToggleButton-root': { minHeight: 44, flex: 1, px: 2 } }}
        >
          {PERIODS.map((p) => (
            <ToggleButton key={p.value} value={p.value} data-testid={`qa-modules-window-${p.value}`}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={problemsOnly ? 'problems' : 'all'}
          onChange={(_, v: string | null) => v && setProblemsOnly(v === 'problems')}
          aria-label="보기"
          sx={{ '& .MuiToggleButton-root': { minHeight: 44, flex: 1, px: 1.5 } }}
        >
          <ToggleButton value="problems" data-testid="qa-modules-problems">
            문제만
          </ToggleButton>
          <ToggleButton value="all" data-testid="qa-modules-all">
            전체
          </ToggleButton>
        </ToggleButtonGroup>
        <TextField
          select
          size="small"
          label="층"
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          SelectProps={{ MenuProps: { disableScrollLock: true } }}
          sx={{ '& .MuiInputBase-root': { minHeight: 44 }, '& .MuiInputBase-input': { fontSize: 16 } }}
        >
          <MenuItem value="">전체 층</MenuItem>
          {levels.map((l) => (
            <MenuItem key={l} value={l}>
              {l}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
      {report && report.scanAt === 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="qa-modules-no-scan">
          이 현장 조명서버에는 모듈 통신 점검 기능이 없거나 아직 한 바퀴도 돌지 않았습니다. 조명서버를 통신
          점검(modhealth)이 들어간 버전으로 올려야 기록이 쌓입니다.
        </Alert>
      )}
      {report && scanAge > SCAN_STALE_SECONDS && (
        <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="qa-modules-stale">
          조명서버 점검이 {Math.round(scanAge / 60)}분째 멈춰 있습니다 — 아래 기록은 그 전까지의 값입니다.
        </Alert>
      )}

      {report && (
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mt: 1.5 }} data-testid="qa-modules-counts">
          {MODULE_VERDICT_ORDER.filter((v) => report.counts[v]).map((v) => (
            <Chip
              key={v}
              size="small"
              color={MODULE_VERDICT_COLOR[v]}
              variant={v === 'ok' ? 'outlined' : 'filled'}
              label={`${MODULE_VERDICT_LABEL[v]} ${report.counts[v]}${v === 'no_modules' ? '대' : '개'}`}
            />
          ))}
        </Stack>
      )}
      {report && shown.length === 0 && !error && (
        <Alert severity="success" sx={{ mt: 1 }}>
          {problemsOnly ? '문제가 있는 CCTV 가 없습니다.' : 'CCTV 가 없습니다.'}
        </Alert>
      )}

      <Box sx={{ mt: 1 }}>
        {shown.map((c) => (
          <Box key={c.name} sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }} data-testid="qa-modules-cctv">
            <Box
              component="button"
              type="button"
              onClick={() => onOpen(c.name)}
              sx={{
                display: 'flex',
                width: '100%',
                minHeight: 44,
                alignItems: 'center',
                gap: 1,
                px: 0.5,
                border: 0,
                bgcolor: 'transparent',
                color: 'text.primary',
                font: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} noWrap>
                  {c.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }} noWrap component="div">
                  {[c.level, c.ip].filter(Boolean).join(' · ') || '현장 DB 에 없는 CCTV'}
                </Typography>
              </Box>
              <Chip size="small" color={MODULE_VERDICT_COLOR[c.verdict]} label={MODULE_VERDICT_LABEL[c.verdict]} />
            </Box>
            {c.modules.length === 0 && (
              <Typography variant="caption" color="text.secondary" component="p" sx={{ px: 0.5 }}>
                조명망 모듈(127·128·통로등)이 등록돼 있지 않아 점검하지 않습니다 — 조명서버 초기화로 CCTV 의
                조명망을 다시 읽어야 합니다.
              </Typography>
            )}
            {c.modules.map((m) => (
              <Box
                key={m.modId}
                sx={{ display: 'flex', alignItems: 'baseline', gap: 1, px: 0.5, py: 0.25 }}
                data-testid="qa-modules-module"
              >
                <Typography variant="caption" fontWeight={700} sx={{ width: 68, flexShrink: 0, color: textColor(m.verdict) }}>
                  {moduleLabel(m)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
                  {MODULE_VERDICT_LABEL[m.verdict]} · {describeModule(m, report?.windowMinutes ?? 0, report?.aisleSilentMinutes ?? 0)}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default QaModulePanel;
